import React, { useState } from 'react'
import { Drawer, List, Avatar, Tag, Button, Divider, Badge, Space, Tooltip, Empty } from 'antd'
import { 
  BellOutlined, 
  PhoneOutlined, 
  UserOutlined, 
  RobotOutlined,
  CheckOutlined,
  DeleteOutlined,
  SettingOutlined,
  CloseOutlined
} from '@ant-design/icons'
import { motion, AnimatePresence } from 'framer-motion'
import { useWebSocket } from '../../contexts/WebSocketContext'

interface NotificationPanelProps {
  open: boolean
  onClose: () => void
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({ open, onClose }) => {
  const { notifications } = useWebSocket()
  const [filter, setFilter] = useState<'all' | 'unread' | 'calls' | 'system'>('all')

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'call-started':
      case 'call-ended':
      case 'call-transferred':
        return <PhoneOutlined className="text-blue-500" />
      case 'system-alert':
        return <BellOutlined className="text-orange-500" />
      case 'ai-insight':
        return <RobotOutlined className="text-purple-500" />
      default:
        return <BellOutlined className="text-gray-500" />
    }
  }

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'call-started': return 'blue'
      case 'call-ended': return 'green'
      case 'call-transferred': return 'purple'
      case 'system-alert': return 'orange'
      case 'ai-insight': return 'cyan'
      default: return 'default'
    }
  }

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'all') return true
    if (filter === 'calls') return notification.type.includes('call')
    if (filter === 'system') return notification.type === 'system-alert'
    return true
  })

  const markAsRead = (id: number) => {
    // Implementation would update notification status
    console.log('Marking as read:', id)
  }

  const deleteNotification = (id: number) => {
    // Implementation would remove notification
    console.log('Deleting notification:', id)
  }

  const markAllAsRead = () => {
    // Implementation would mark all as read
    console.log('Marking all as read')
  }

  const clearAll = () => {
    // Implementation would clear all notifications
    console.log('Clearing all notifications')
  }

  return (
    <Drawer
      title={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BellOutlined className="text-xl" />
            <span>Notifications</span>
            <Badge count={notifications.length} size="small" />
          </div>
          <Space>
            <Tooltip title="Mark all as read">
              <Button 
                type="text" 
                icon={<CheckOutlined />} 
                size="small"
                onClick={markAllAsRead}
              />
            </Tooltip>
            <Tooltip title="Clear all">
              <Button 
                type="text" 
                icon={<DeleteOutlined />} 
                size="small"
                onClick={clearAll}
              />
            </Tooltip>
            <Tooltip title="Settings">
              <Button 
                type="text" 
                icon={<SettingOutlined />} 
                size="small"
              />
            </Tooltip>
          </Space>
        </div>
      }
      placement="right"
      onClose={onClose}
      open={open}
      width={400}
      className="[&_.ant-drawer-content]:bg-slate-800/95 [&_.ant-drawer-content]:backdrop-blur-sm"
    >
      {/* Filter Buttons */}
      <div className="mb-4">
        <Space wrap>
          {['all', 'calls', 'system'].map((filterType) => (
            <Button
              key={filterType}
              size="small"
              type={filter === filterType ? 'primary' : 'default'}
              onClick={() => setFilter(filterType as any)}
              className={filter === filterType ? 'bg-purple-600' : ''}
            >
              {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
            </Button>
          ))}
        </Space>
      </div>

      <Divider className="border-gray-600" />

      {/* Notifications List */}
      <div className="space-y-3">
        <AnimatePresence>
          {filteredNotifications.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Empty 
                description="No notifications"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className="text-gray-400"
              />
            </motion.div>
          ) : (
            filteredNotifications.map((notification) => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="text-white font-medium mb-1">
                          {notification.title}
                        </div>
                        <div className="text-gray-300 text-sm">
                          {notification.message}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1 ml-2">
                        <Tag 
                          color={getNotificationColor(notification.type)} 
                         
                        >
                          {notification.type.replace('-', ' ')}
                        </Tag>
                        <Button
                          type="text"
                          icon={<CloseOutlined />}
                          size="small"
                          onClick={() => deleteNotification(notification.id)}
                          className="text-gray-400 hover:text-white"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        {notification.timestamp.toLocaleTimeString()}
                      </div>
                      
                      {notification.data && (
                        <Button
                          type="link"
                          size="small"
                          className="text-blue-400 p-0 h-auto"
                          onClick={() => markAsRead(notification.id)}
                        >
                          View Details
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Severity indicator for system alerts */}
                {notification.severity && (
                  <div className="mt-2 pt-2 border-t border-gray-600">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        notification.severity === 'error' ? 'bg-red-500' :
                        notification.severity === 'warning' ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`} />
                      <span className="text-xs text-gray-400 capitalize">
                        {notification.severity} Priority
                      </span>
                    </div>
                  </div>
                )}
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>
    </Drawer>
  )
}
